import { useRef, useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Input } from 'antd';
import type { MFAInputProps } from './types';
import './MFAInput.css';

/**
 * MFAInput Component
 *
 * A specialized input component for Multi-Factor Authentication (MFA) code verification
 * with multiple individual input fields that behave as a single unified input.
 *
 * ## Features
 * - **Auto-focus Navigation**: Automatically moves to next field on input
 * - **Paste Support**: Handles clipboard paste across all fields
 * - **Keyboard Navigation**: Arrow keys, Backspace, and Delete support
 * - **Alphanumeric Input**: Accepts both numbers and letters (0-9, a-z, A-Z)
 * - **Error States**: Visual feedback for validation errors
 * - **Accessibility**: Proper ARIA labels and keyboard support
 *
 * ## Component Flow
 *
 * ```mermaid
 * flowchart TD
 *     A[User Input] --> B{Action?}
 *     B -->|Type| C[Sanitize]
 *     C --> D[Update State]
 *     D --> E[Focus Next]
 *     B -->|Backspace| F[Clear & Move Back]
 *     B -->|Paste| G[Fill All Fields]
 *     E --> H{Complete?}
 *     F --> H
 *     G --> H
 *     H -->|Yes| I[onComplete]
 *     H -->|No| J[Wait]
 * ```
 *
 * ## State Flow
 *
 * ```mermaid
 * sequenceDiagram
 *     Parent->>Component: value prop
 *     Component->>State: sync
 *     User->>Component: input
 *     Component->>Parent: onChange
 *     Component->>Parent: onComplete
 * ```
 *
 * @example
 * Basic usage
 * ```tsx
 * <MFAInput
 *   value={code}
 *   onChange={setCode}
 *   onComplete={(code) => verifyCode(code)}
 *   error={hasError}
 * />
 * ```
 *
 * @example
 * Custom length
 * ```tsx
 * <MFAInput value={code} onChange={setCode} length={4} />
 * ```
 */
const MFAInput: React.FC<MFAInputProps> = ({
  value,
  onChange,
  onComplete,
  length = 6,
  error = false,
  disabled = false,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [localValues, setLocalValues] = useState<string[]>(
    Array(length).fill('')
  );

  // Sync external value with local state
  useEffect(() => {
    const chars = value.padEnd(length, '').split('').slice(0, length);
    setLocalValues(chars);
  }, [value, length]);

  // Call onComplete when all fields are filled
  useEffect(() => {
    const completeValue = localValues.join('');
    if (completeValue.length === length && completeValue.replace(/\s/g, '')) {
      onComplete?.(completeValue);
    }
  }, [localValues, length, onComplete]);

  const focusInput = (index: number) => {
    if (index >= 0 && index < length) {
      inputRefs.current[index]?.focus();
      inputRefs.current[index]?.select();
    }
  };

  const handleChange = (index: number, inputValue: string) => {
    // Only allow single digit/character
    const sanitized = inputValue.replace(/[^0-9a-zA-Z]/g, '').slice(0, 1);

    const newValues = [...localValues];
    newValues[index] = sanitized;
    setLocalValues(newValues);
    onChange(newValues.join(''));

    // Auto-focus next input if value entered
    if (sanitized && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault();

      if (localValues[index]) {
        // Clear current field
        const newValues = [...localValues];
        newValues[index] = '';
        setLocalValues(newValues);
        onChange(newValues.join(''));
      } else if (index > 0) {
        // Move to previous field and clear it
        const newValues = [...localValues];
        newValues[index - 1] = '';
        setLocalValues(newValues);
        onChange(newValues.join(''));
        focusInput(index - 1);
      }
    }
    // Handle left arrow
    else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    }
    // Handle right arrow
    else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
    // Handle delete
    else if (e.key === 'Delete') {
      e.preventDefault();
      const newValues = [...localValues];
      newValues[index] = '';
      setLocalValues(newValues);
      onChange(newValues.join(''));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const sanitized = pastedData.replace(/[^0-9a-zA-Z]/g, '').slice(0, length);

    const chars = sanitized.split('');
    const newValues = [...localValues];

    chars.forEach((char, i) => {
      if (i < length) {
        newValues[i] = char;
      }
    });

    setLocalValues(newValues);
    onChange(newValues.join(''));

    // Focus last filled input or first empty
    const nextEmptyIndex = newValues.findIndex((v) => !v);
    focusInput(nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex);
  };

  const handleFocus = (index: number) => {
    inputRefs.current[index]?.select();
  };

  return (
    <div className="mfa-input-container">
      {Array.from({ length }).map((_, index) => (
        <Input
          key={index}
          ref={(el) => {
            if (el?.input) {
              inputRefs.current[index] = el.input;
            }
          }}
          value={localValues[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          maxLength={1}
          className={`mfa-input-field ${error ? 'mfa-input-error' : ''}`}
          aria-label={`MFA code digit ${index + 1}`}
          autoComplete="off"
          inputMode="text"
        />
      ))}
    </div>
  );
};

export default MFAInput;
